<template>
  <q-select
    ref="crudSelect"
    v-model="cModelValue"
    :label="label"
    use-input
    :behavior="behavior"
    :debounce="100"
    :options="filteredResults"
    :clearable="true"
    :outlined="!borderless"
    :dark="dark || $q.dark.isActive"
    dense
    :color="color"
    :use-chips="multiple"
    :multiple="multiple"
    :bg-color="bgColor"
    :label-color="labelColor"
    :option-label="labelField"
    :option-value="valueField"
    :square="square"
    :hide-bottom-space="true"
    :borderless="borderless"
    emit-value
    map-options
    @input-value="(e) => (inputValue = e)"
    @filter="filterResults"
    @update:model-value="handleSelection">
    <template #no-option>
      <slot name="noResultOption">
        <q-item>
          <q-item-section class="text-grey">Aucun résultat</q-item-section>
        </q-item>
      </slot>
    </template>
    <template #option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section
          v-if="recentlySelected.includes(scope.opt[valueField])"
          side>
          <q-icon
            :name="symRoundedThumbUp"
            class="text-positive selection-icon">
            <q-tooltip>Selected</q-tooltip>
          </q-icon>
        </q-item-section>
        <q-item-section>
          <component
            :is="crudSelectOptionComponent"
            v-if="crudSelectOptionComponent"
            :option="scope.opt"
            :search-term="inputValue" />
          <span v-else>{{ scope.opt[labelField] }}</span>
        </q-item-section>
      </q-item>
      <q-separator />
    </template>
    <template #after>
      <q-btn
        v-if="canAdd"
        :icon="symRoundedAdd"
        dense
        flat
        color="info"
        @click="createObject" />
      <q-btn
        v-if="canRefresh"
        :icon="symRoundedRefresh"
        dense
        flat
        color="info"
        @click="refresh" />
    </template>
    <template
      v-if="cModelValue"
      #selected>
      <div
        v-if="Array.isArray(cModelValue) && multiple"
        class="row q-gutter-sm items-start q-pt-md">
        <div
          v-for="id in cModelValue"
          :key="id"
          class="col-auto q-pr-md">
          <CrudPreview
            :model-id="id"
            :model-name="modelName" />
        </div>
      </div>
      <div v-else-if="cModelValue && !Array.isArray(cModelValue)">
        <CrudPreview
          :model-id="cModelValue"
          :model-name="modelName" />
      </div>
    </template>

    <template #before-options>
      <slot name="before-close-option"></slot>
      <q-list
        bordered
        separator>
        <q-item
          class="bg-yellow-2 q-mt-none text-black text-weight-medium"
          :class="autoClose ? 'bg-green-2' : 'bg-yellow-2'">
          <q-item-section>
            <q-item-label>Fermer auto. après la sélection</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-item-label>
              <q-toggle
                v-model="autoClose"
                dense
                color="primary" />
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item
          v-if="isMobile && !autoClose"
          class="bg-yellow-3 q-mt-none"
          dense>
          <q-item-section>
            <q-btn
              v-close-popup
              dense
              flat
              :icon="symRoundedClose"
              class=""
              color="warning"
              label="Fermer ce menu"></q-btn>
          </q-item-section>
        </q-item>
      </q-list>
    </template>
  </q-select>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'

import { symRoundedAdd, symRoundedClose, symRoundedRefresh, symRoundedThumbUp } from '@quasar/extras/material-symbols-rounded'
import { Platform, useQuasar } from 'quasar'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import CrudPreview from 'src/CrudManager/Core/composites/Editors/CrudPreview/CrudPreview.vue'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudObject, Filters } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'

const $q = useQuasar()
const crudManagerStore = useCrudManagerStore()
const { getModelConfig } = useCrudUtils()

const appSettingsStore = useAppSettingsStore()

const emit = defineEmits(['update:modelValue', 'addItem', 'update:data'])

interface Props {
  modelValue?: string | number | string[] | null
  modelName: string
  index?: number | null
  dataType?: string
  lookupdata?: Record<string, unknown>
  labelField?: string
  valueField?: string
  legacy?: boolean
  label?: string
  square?: boolean
  dark?: boolean
  borderless?: boolean
  multiple?: boolean
  color?: string | null
  bgColor?: string | null
  labelColor?: string
  canAdd?: boolean
  canRefresh?: boolean
  filters?: Filters[]
  limit?: number
  with?: string[]
  removeOnSelect?: boolean
  autoCloseDefault?: boolean
}

const {
  modelValue = null,
  modelName,
  index = null,
  dataType = '',
  lookupdata = {},
  labelField = 'nom',
  valueField = 'id',
  legacy = false,
  label = 'PleaseSetLabel',
  square = false,
  dark = false,
  borderless = false,
  multiple = false,
  color = null,
  bgColor = null,
  labelColor = '',
  canAdd = false,
  canRefresh = true,
  filters = [],
  limit = 10,
  with: withRelations = [],
  removeOnSelect = false,
  autoCloseDefault = true,
} = defineProps<Props>()

const inputValue = ref('')
const crudSelectOptionComponent = ref<Component | null>(null)
const modelSortBy = ref('created_at')
const modelSortDirection = ref('desc')
const isReady = ref(false)
const list = ref([] as CrudObject[])
const filteredResults = ref([])
const cModelValue = computed({
  get: () => modelValue,
  set: (e) => emit('update:modelValue', e),
})

const offset = index && lookupdata.limit ? (index - 1) * (lookupdata.limit as number) : 0
const crudSelect = ref(null)
const focus = () => {
  if (crudSelect.value) {
    crudSelect.value.focus()
  }
}
defineExpose({ focus })

watch(
  () => filters,
  async () => {
    if (!isReady.value) return
    await getResult()
  },
  { deep: true },
)

const selectedItemsData = (e: unknown) => {
  const cleared = e === null || e === undefined || e === '' || (Array.isArray(e) && e.length === 0)
  if (cleared) {
    recentlySelected.value = []
    emit('update:data', multiple ? [] : null)
    emit('update:modelValue', multiple ? [] : null)
    return
  }

  emit('update:modelValue', e as string | number | string[])

  const data = Array.isArray(e) ? list.value.filter((opt) => e.includes(opt[valueField])) : list.value.filter((opt) => opt[valueField] === e)

  if (removeOnSelect) {
    cModelValue.value = multiple ? [] : null
    emit('addItem', data)
    return
  }
  // Always emit the filtered data, even if it's empty
  emit('update:data', multiple ? data : data[0] || null)
}

const getResult = async (query = null) => {
  try {
    const postUrl = legacy ? `${modelName}` : 'cm/selector'

    const postParams = {
      query: query || '',
      model: modelName,
      labelField: labelField,
      valueField: valueField,
      offset: offset,
      limit: limit,
      filters: filters,
      with: withRelations,
      sortBy: modelSortBy.value,
      sortDirection: modelSortDirection.value,
    }

    const response = await axiosApi.post(postUrl, postParams)
    list.value = response.data.hits || []
    filteredResults.value = response.data.hits || []
  } catch (error) {
    console.warn('Error fetching results:', error)
    list.value = []
    filteredResults.value = []
  }
}

const filterResults = function (val, update) {
  getResult(val).then(() => {
    update(() => {
      // Ensure filteredResults is always an array
      filteredResults.value = list.value || []
    })
  })
}

const createObject = async (event) => {
  event.stopPropagation()
  event.preventDefault()

  try {
    await crudManagerStore.createInstance(modelName, 'creator', { useDialog: true })

    const instanceData = crudManagerStore.lastClosedInstance?.data
    if (instanceData && 'id' in instanceData) {
      cModelValue.value = instanceData.id as string | number
    }

    // reset the last closed instance
    crudManagerStore.lastClosedInstance = null

    await loadAndEmitData()

    // Close the dropdown if auto-close is enabled
    if (autoClose.value && crudSelect.value) {
      crudSelect.value.hidePopup()
    }
  } catch (error) {
    console.error(`Error creating new ${label}:`, error)
    showErrorNotification(`Failed to create new ${label}`, error)
  }
}

const refresh = async (event) => {
  event.stopPropagation()
  event.preventDefault()
  await getResult()

  showSuccessNotification('Liste mise à jour avec succès!')
}

// New function to load and emit data
const loadAndEmitData = async () => {
  await getResult()
  if (modelValue) {
    selectedItemsData(modelValue)
  }
}

const handleSelection = (newValue) => {
  if (newValue === null || newValue === undefined || newValue === '') {
    recentlySelected.value = []
    selectedItemsData(newValue)
    return
  }

  if (Array.isArray(newValue)) {
    const newlySelected = newValue.filter((v) => !recentlySelected.value.includes(v))
    recentlySelected.value = [...recentlySelected.value, ...newlySelected]
  } else if (newValue && !recentlySelected.value.includes(newValue)) {
    recentlySelected.value.push(newValue)
  }

  // Clear the visual selection after a short delay
  setTimeout(() => {
    recentlySelected.value = []
  }, 1000) // Adjust this delay as needed

  selectedItemsData(newValue)

  if (removeOnSelect) {
    showSuccessNotification('Objet ajouté avec succès')
  }
  // Close the dropdown if auto-close is enabled
  if (autoClose.value && crudSelect.value) {
    crudSelect.value.hidePopup()
  }
}

const recentlySelected = ref([])

const behavior = computed(() => (Platform.is.mobile ? 'dialog' : 'menu'))
const isMobile = computed(() => Platform.is.mobile)

const autoClose = appSettingsStore.getUserSettings('crudSelectAutoClose', autoCloseDefault)

watch(autoClose, (newValue) => {
  appSettingsStore.setUserSettings('crudSelectAutoClose', newValue)
})

onMounted(async () => {
  const modelConfig = await getModelConfig(modelName)
  crudSelectOptionComponent.value = modelConfig.crudSelect?.components?.option
  modelSortBy.value = modelConfig.crudSelect?.sortBy || 'created_at'
  modelSortDirection.value = modelConfig.crudSelect?.sortDirection || 'desc'
  // Initialize data first
  await getResult()

  if (modelValue) {
    await loadAndEmitData()
  }

  // if the dataType is set and modelName is not, use dataType
  if (dataType && !modelName) {
    console.warn('Using dataType as modelName is deprecated. Please use modelName prop instead.')
  }
  isReady.value = true
})
</script>

<style scoped>
.q-item__section--side .q-icon {
  transition:
    transform 0.3s,
    opacity 0.3s;
}

.q-item__section--side .q-icon:not([name='none']) {
  transform: scale(1.2);
  opacity: 1;
}

.q-item__section--side .q-icon[name='none'] {
  transform: scale(0);
  opacity: 0;
}

.selection-icon {
  transition:
    transform 0.3s,
    opacity 0.3s;
}

.selection-icon[name='symRoundedThumbUp'] {
  transform: scale(1.2);
  opacity: 1;
}

.selection-icon[name='none'] {
  transform: scale(0);
  opacity: 0;
}
</style>
